/**
 * CustomerPortalAccessPage: `/customer-portal`, staff's direct way into a
 * customer's portal. Replaces the old Settings > Customer portal screen
 * (docs/features/customer-storefront/12_decision_access_for_every_customer.md).
 *
 * Two jobs:
 *   1. Jump. Type any customer and land on that customer's People tab inside
 *      the portal's staff view (/portal-view/customer/:id/people), where who
 *      is linked, their extras and the portal block are managed beside "view
 *      as". Staff start from "the customer on the phone", not from a
 *      customer's page, so this is a nav item rather than a button on one.
 *   2. Cross-customer view: what no single customer's page can show. Access
 *      awaiting approval from the move off organisations, customers whose
 *      portal is blocked, and who signed in lately.
 */
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import type {
  CustomerPortalPeopleAdapter,
  PortalAccessOverview,
} from "@shared-core/data/customer-portal-access-types";
import { Card } from "../app/components/primitives/card";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Skeleton } from "../app/components/primitives/skeleton";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";
import { EntityCombobox } from "../app/components/patterns/EntityCombobox";

/** The staff view is a separate bundle on the same origin: a full navigation, not a route change. */
function peopleTabUrl(customerId: string): string {
  return `/portal-view/customer/${encodeURIComponent(customerId)}/people`;
}

function ago(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-3 p-5">
      <div>
        <h2 className="font-medium">{title}</h2>
        {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </Card>
  );
}

function AwaitingApproval({
  overview,
  adapter,
  onDecided,
}: {
  overview: PortalAccessOverview;
  adapter: CustomerPortalPeopleAdapter;
  onDecided: () => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const items = overview.widening_awaiting_approval;
  const canManage = overview.can_manage_access;

  async function decide(
    item: (typeof items)[number],
    decision: "approve" | "keep_narrow",
  ) {
    const key = `${item.person_id}:${item.customer_id}`;
    setBusyKey(key);
    try {
      await adapter.decidePortalWidening(item.hold_id, decision);
      toast.success(
        decision === "approve"
          ? `${item.display_name} now has ${item.would_become} at ${item.customer_name}`
          : `${item.display_name} stays on ${item.today} at ${item.customer_name}`,
      );
      onDecided();
    } catch {
      toast.error("We could not save that decision. Try again.");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <Section
      title="Wider access awaiting approval"
      hint="When portal organisations were retired, these people would have been given more than they have now. They stay on own work until you decide."
    >
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing is waiting for a decision.
        </p>
      ) : (
        <ul className="divide-y">
          {items.map((item) => {
            const key = `${item.person_id}:${item.customer_id}`;
            return (
              <li
                key={key}
                className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 space-y-0.5 text-sm">
                  <p className="font-medium">
                    {item.display_name}{" "}
                    <span className="font-normal text-muted-foreground">
                      at {item.customer_name} · {item.email}
                    </span>
                  </p>
                  <p className="text-muted-foreground">
                    Today: {item.today}. Approving gives: {item.would_become}.
                  </p>
                </div>
                <div className="flex gap-2">
                  {!canManage && (
                    <p className="self-center text-xs text-muted-foreground">
                      Only an admin can decide.
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyKey === key || !canManage}
                    onClick={() => void decide(item, "keep_narrow")}
                  >
                    Keep own work only
                  </Button>
                  <Button
                    size="sm"
                    disabled={busyKey === key || !canManage}
                    onClick={() => void decide(item, "approve")}
                  >
                    Approve wider access
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function Overview({
  overview,
  adapter,
  reload,
}: {
  overview: PortalAccessOverview;
  adapter: CustomerPortalPeopleAdapter;
  reload: () => void;
}) {
  const capabilities = usePlatformCapabilities();
  const notSignedIn = overview.people_count - overview.people_signed_in_count;
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {overview.people_count} people at {overview.customers_with_people_count}{" "}
        customers: {overview.people_signed_in_count} have signed in,{" "}
        {notSignedIn} have not yet. Every other customer can use the portal too;
        people are added the first time someone is picked as a contact or signs
        in.
      </p>

      <AwaitingApproval
        overview={overview}
        adapter={adapter}
        onDecided={reload}
      />

      <Section
        title="Blocked customers"
        hint="Nobody linked to these customers can open their portal. The reason is for staff only."
      >
        {overview.blocked_customers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No customer&apos;s portal is blocked.
          </p>
        ) : (
          <ul className="divide-y">
            {overview.blocked_customers.map((b) => (
              <li
                key={b.customer_id}
                className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="space-y-0.5 text-sm">
                  <p className="font-medium">
                    {b.customer_name} <Badge variant="error">Blocked</Badge>
                  </p>
                  <p className="text-muted-foreground">{b.block.reason}</p>
                  <p className="text-xs text-muted-foreground">
                    {b.block.blocked_by}, {formatDateOrDash(b.block.blocked_at)}{" "}
                    · {b.people_count}{" "}
                    {b.people_count === 1 ? "person" : "people"} affected
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    capabilities.redirectTo(peopleTabUrl(b.customer_id))
                  }
                >
                  Open
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Recent sign-ins">
        <ul className="divide-y text-sm">
          {overview.recent_sign_ins.map((s) => (
            <li
              key={s.person_id}
              className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {s.display_name}
                  {s.is_first_sign_in && (
                    <Badge variant="success" className="ml-2">
                      First time
                    </Badge>
                  )}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {s.customer_names.join(", ")}
                </p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {ago(s.signed_in_at)}
              </span>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}

export function CustomerPortalAccessPage() {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const { state, retry } = useListQuery(
    () => dataAdapter.getPortalAccessOverview(),
    [dataAdapter],
  );

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">
          Customer portal
        </h1>
        <p className="text-sm text-muted-foreground">
          Every customer can use the portal. Each person sees their own work;
          you can add extras for people who need more, or block a customer.
        </p>
      </div>

      <Card className="space-y-2 p-5">
        <h2 className="text-sm font-medium">Open a customer&apos;s portal</h2>
        <EntityCombobox
          value={null}
          clearable={false}
          ariaLabel="Open a customer's portal"
          placeholder="Search customers by name…"
          fetchOptions={async (q) => {
            // The customer spine, so a CIS-only customer is found too; its id is the CIS id the
            // staff view is keyed by.
            const result = await dataAdapter.listCisCustomers({
              q: q || undefined,
              pageSize: 20,
            });
            return result.items.map((c) => ({
              id: c.id,
              label: c.name,
              hint: c.tharstern_customer_code ?? c.customer_code,
            }));
          }}
          onChange={(customer) => {
            if (customer) capabilities.redirectTo(peopleTabUrl(customer.id));
          }}
        />
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ExternalLink className="mt-0.5 size-3.5 shrink-0" />
          Opens the customer&apos;s portal in staff view, on its People tab: who
          can use it, what each person can do, and whether it is blocked.
          Orders, spend and proofs are one click along.
        </p>
      </Card>

      {state.status === "loading" && (
        <Card className="space-y-3 p-5">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-24 w-full" />
        </Card>
      )}
      {state.status === "error" && (
        <ErrorPanel message={state.message} onRetry={retry} />
      )}
      {state.status === "success" && (
        <Overview overview={state.data} adapter={dataAdapter} reload={retry} />
      )}
    </div>
  );
}
